import * as Headless from '@headlessui/react';
import {
  OtsComboboxOptions,
  isOtsComboboxOptionDisabled,
} from './OtsComboboxOptions';
import {
  CheckIcon,
  ChevronUpDownIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid';
import { KeyboardEvent, useId, useMemo, useState } from 'react';
import type { OtsComboboxOption } from './OtsCombobox';
import { OtsFormField, OtsFormFieldSize } from './OtsFormField';

export type OtsMultiSelectProps = {
  id?: string;
  name?: string;
  label: string;
  description?: string;
  options: OtsComboboxOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  loading?: boolean;
  error?: string;
  disabled?: boolean;
  searchable?: boolean;
  size?: OtsFormFieldSize;
  emptyMessage?: string;
  className?: string;
};

/** Multi-value combobox with searchable options and removable selection chips. */
export const OtsMultiSelect = ({
  id: idProp,
  name,
  label,
  description,
  options,
  value,
  onChange,
  placeholder = 'Select one or more options',
  loading = false,
  error,
  disabled = false,
  searchable = true,
  size = 'default',
  emptyMessage = 'No matching options.',
  className,
}: OtsMultiSelectProps) => {
  const generatedId = useId();
  const id = idProp ?? `ots-multi-combobox-${generatedId}`;
  const descriptionId = description ? `${id}-description` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [descriptionId, errorId].filter(Boolean).join(' ');
  const [query, setQuery] = useState('');
  const compact = size === 'compact';
  const selected = useMemo(
    () => options.filter((option) => value.includes(option.value)),
    [options, value]
  );
  const filtered = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    return normalized
      ? options.filter(
          (option) =>
            option.label.toLocaleLowerCase().includes(normalized) ||
            option.value.toLocaleLowerCase().includes(normalized) ||
            option.description?.toLocaleLowerCase().includes(normalized)
        )
      : options;
  }, [options, query]);
  const unavailable = disabled || loading;

  const remove = (optionValue: string) =>
    onChange(value.filter((selectedValue) => selectedValue !== optionValue));

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && query === '' && value.length) {
      remove(value[value.length - 1]);
    }
  };

  const renderOption = (option: OtsComboboxOption) => (
    <Headless.ComboboxOption
      key={option.value}
      value={option}
      disabled={option.disabled}
      className={[
        'group flex cursor-default items-center gap-2 rounded-md px-2.5 data-focus:bg-[var(--ots-primary-container)] data-focus:text-[var(--ots-on-primary-container)] data-disabled:opacity-50',
        compact ? 'min-h-8 text-xs' : 'min-h-9 text-sm',
      ].join(' ')}
    >
      <span className="min-w-0 flex-1">
        <span className="block truncate">{option.label}</span>
        {option.description && (
          <span className="block truncate text-[11px] text-[var(--ots-text-muted)] group-data-focus:text-inherit">
            {option.description}
          </span>
        )}
      </span>
      <CheckIcon
        aria-hidden="true"
        className="invisible size-4 shrink-0 group-data-selected:visible"
      />
    </Headless.ComboboxOption>
  );

  return (
    <OtsFormField
      htmlFor={id}
      label={label}
      description={description}
      descriptionId={descriptionId}
      error={error}
      errorId={errorId}
      size={size}
      className={className}
    >
      {name &&
        value.map((selectedValue) => (
          <input
            key={selectedValue}
            type="hidden"
            name={name}
            value={selectedValue}
            disabled={unavailable}
          />
        ))}
      <Headless.Combobox
        value={selected}
        onChange={(next) => {
          onChange(next.map((option) => option.value));
          setQuery('');
        }}
        multiple
        disabled={unavailable}
        onClose={() => setQuery('')}
        virtual={{ options: filtered, disabled: isOtsComboboxOptionDisabled }}
      >
        <div className="relative min-w-0">
          <div
            className={[
              'box-border flex w-full min-w-0 flex-wrap items-center gap-1.5 rounded-lg border border-[var(--ots-field-outline)] bg-[var(--ots-field)] text-[var(--ots-text)] shadow-sm transition focus-within:border-[var(--ots-primary)] focus-within:ring-2 focus-within:ring-[var(--ots-primary-container)]',
              compact ? 'min-h-8 px-2 py-1' : 'min-h-10 px-2.5 py-1.5',
              unavailable
                ? 'cursor-not-allowed bg-[var(--ots-surface-variant)] opacity-65'
                : '',
            ].join(' ')}
          >
            {selected.map((option) => (
              <span
                key={option.value}
                className={[
                  'inline-flex max-w-full items-center gap-1 rounded-md bg-[var(--ots-primary-container)] px-1.5 text-[var(--ots-on-primary-container)]',
                  compact ? 'min-h-5 text-[11px]' : 'min-h-6 text-xs',
                ].join(' ')}
              >
                <span className="truncate">{option.label}</span>
                {!unavailable && (
                  <button
                    type="button"
                    aria-label={`Remove ${option.label}`}
                    className="shrink-0 rounded-sm hover:bg-black/10 focus:outline-none focus:ring-1 focus:ring-current"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => remove(option.value)}
                  >
                    <XMarkIcon aria-hidden="true" className="size-3.5" />
                  </button>
                )}
              </span>
            ))}
            <Headless.ComboboxInput
              id={id}
              aria-label={label}
              aria-describedby={describedBy || undefined}
              aria-invalid={error ? true : undefined}
              value={query}
              readOnly={!searchable}
              onChange={(event) => setQuery(event.currentTarget.value)}
              onKeyDown={handleInputKeyDown}
              placeholder={
                loading
                  ? 'Loading options…'
                  : selected.length
                  ? 'Add another…'
                  : placeholder
              }
              className={[
                'min-w-24 flex-1 border-0 bg-transparent p-0 text-[var(--ots-text)] outline-none placeholder:text-[var(--ots-text-muted)] focus:ring-0',
                compact ? 'text-xs' : 'text-sm',
              ].join(' ')}
            />
            <Headless.ComboboxButton
              aria-label={`Open ${label} options`}
              className="ml-auto flex shrink-0 items-center text-[var(--ots-text-muted)] hover:text-[var(--ots-text)] disabled:cursor-not-allowed"
            >
              <ChevronUpDownIcon aria-hidden="true" className="size-4" />
            </Headless.ComboboxButton>
          </div>
          <OtsComboboxOptions
            options={filtered}
            renderOption={renderOption}
            emptyMessage={query ? 'No matching options.' : emptyMessage}
            className="absolute z-50 mt-1 max-h-60 w-full min-w-0 overflow-auto rounded-lg border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)] p-1 text-[var(--ots-text)] shadow-xl focus:outline-none"
          />
        </div>
      </Headless.Combobox>
      {loading && (
        <p role="status" className="text-xs text-[var(--ots-text-muted)]">
          Loading options…
        </p>
      )}
    </OtsFormField>
  );
};
